/**
 * Who is signed in.
 *
 * The console never decides this for itself: it asks `/v1/auth/session` and
 * believes the answer. A 401 is the ordinary "not signed in" and is not an
 * error to show — treating it as one is how a first visit looks broken.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { Feature, Session } from '@tomo/contracts';
import { ApiError } from './lib/api.js';
import { auth } from './lib/tomo.js';
import { useAsync } from './useAsync.js';
import { Failed, Spinner } from './components/primitives.jsx';
import { SignIn } from './pages/SignIn.jsx';
import { SiteFrame } from './components/SiteFrame.jsx';

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  // Every screen below the gate has one. Throwing beats a nullable context
  // that every component has to remember to narrow.
  if (!session) throw new Error('useSession outside a signed-in tree');
  return session;
}

/**
 * Is this unreleased feature on for the person signed in?
 *
 * Hiding, not refusing: the API does not read flags, and a screen reached by
 * typing its address still works. What a flag decides is what the studio
 * OFFERS, so somebody it is off for never meets half-finished work.
 */
export function useFeature(feature: Feature): boolean {
  return useSession().features.includes(feature);
}

export function SessionGate({ children }: { children: ReactNode }): ReactNode {
  const [result, retry] = useAsync((signal) => auth.session(signal), []);

  /*
   * The gate's own screens sit in the site's frame too. They are the first
   * thing anybody sees of the studio, and a bare page here was the one step
   * between the front page and the studio that looked like neither.
   */
  if (result.state === 'loading') {
    return <SiteFrame viewer={undefined} trail={STUDIO}><Spinner label="Checking your session…" /></SiteFrame>;
  }

  if (result.state === 'failed') {
    // 401 is not a failure. It is the answer.
    if (result.error.status === 401) return <SiteFrame viewer={null} trail={STUDIO}><SignIn /></SiteFrame>;
    return (
      <SiteFrame viewer={null} trail={STUDIO}>
        <div className="max-w-lg">
          <Failed
            message="Could not reach tomo."
            detail={result.error.message}
            onRetry={retry}
          />
        </div>
      </SiteFrame>
    );
  }

  /*
   * EVERY SIGNED-IN ACCOUNT IS LET IN, channels or not.
   *
   * This used to stop here with "this account does not administer a channel
   * yet — connect your Twitch channel and sign in again", on a page with no
   * way to connect one. The way to connect lives on the studio's own channel
   * picker, which this check stood in front of — so the one person who needed
   * that button, a streamer who had not connected yet, could never reach it.
   * The picker has its own screen for owning nothing; the API decides what
   * each account may do with a channel, on every request.
   */
  return <SessionContext value={result.value}>{children}</SessionContext>;
}

const STUDIO = [{ label: 'studio' }];

export { ApiError };
